<template>
  <b-col>
    <b-row>
      <b-col cols="12">
        <div class="d-flex justify-content-between align-items-center pt-50 mb-2">
          <h2 class="font-weight-bolder m-0">
            Calendario
          </h2>
          <span
            class="cursor-pointer"
            @click="toggleFilterSidebar"
          >
            <feather-icon
              icon="FilterIcon"
              size="20"
            />
          </span>
        </div>
      </b-col>
      <b-col cols="12">
        <!-- Sidebar de filtros -->
        <filter-sidebar
          v-model="showFilterSidebar"
          :has-active-filters="hasActiveFilters"
          :active-filters="computedActiveFilters"
          @apply-filters="handleSearch"
          @clear-filters="clearFilters"
          @remove-filter="removeActiveFilter"
        >
          <!-- Slot de filtros -->
          <template #filters>
            <b-form-group
              label="Departamento"
              label-for="department-select"
            >
              <BaseSelect
                v-model="department"
                :searchable="true"
                :clearable="true"
                :multiple="false"
                :http-body="{
                  client_id: currentClient ? currentClient.id : ''
                }"
                :resource="'/departments'"
                :placeholder="'Todos los departamentos'"
              />
            </b-form-group>
            <b-form-group
              label="Usuario"
              label-for="user-select"
            >
              <BaseSelect
                v-model="userfilter"
                :searchable="true"
                :clearable="true"
                :multiple="false"
                :http-body="{
                  client_id: currentClient ? currentClient.id : '',
                  department_id: department ? department.id : ''
                }"
                :resource="'/users'"
                :placeholder="'Todos los usuarios'"
              />
            </b-form-group>
          </template>
        </filter-sidebar>
      </b-col>
    </b-row>
    <b-card>
      <div class="d-flex align-items-center justify-content-between">
        <b-button
          variant="outline-primary"
          size="sm"
          :disabled="currentPage === 0"
          @click="prevPage"
        >
          <feather-icon
            icon="ChevronLeftIcon"
            size="14"
          />
        </b-button>
        <div class="d-flex align-items-center">
          <b-button
            variant="outline-primary"
            size="sm"
            class="me-2"
            :disabled="displayedYear === currentYear"
            @click="prevYear"
          >
            {{ displayedYear - 1 }}
          </b-button>
          <h2 class="mb-0 mx-2">
            {{ displayedYear }}
          </h2>
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="displayedYear === currentYear + 1"
            @click="nextYear"
          >
            {{ displayedYear + 1 }}
          </b-button>
        </div>
        <b-button
          variant="outline-primary"
          size="sm"
          :disabled="currentPage === maxPage"
          @click="nextPage"
        >
          <feather-icon
            icon="ChevronRightIcon"
            size="14"
          />
        </b-button>
      </div>
      <div class="d-flex justify-content-between align-items-center mb-2" />
      <div>
        <div class="d-flex">
          <div
            v-if="pagedMonth"
            class="flex-fill px-1"
          >
            <h4 class="text-center">
              {{ pagedMonth.name }}
            </h4>
            <table class="table table-sm">
              <thead>
                <tr>
                  <th
                    v-for="(day, dIdx) in weekDays"
                    :key="dIdx"
                    class="text-center"
                  >
                    {{ day }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(week, wIdx) in pagedMonth.weeks"
                  :key="wIdx"
                >
                  <td
                    v-for="(day, dIdx) in week"
                    :key="dIdx"
                    class="text-center"
                  >
                    <div
                      v-if="day"
                      class="calendar-day-square"
                    >
                      <span
                        :id="`day-${pagedMonth.index}-${day}`"
                        :class="[
                          isToday(pagedMonth.index, day) ? 'today' : '',
                          holidaysByMonth[pagedMonth.index] && holidaysByMonth[pagedMonth.index][day] ? 'holiday-day' : ''
                        ]"
                      >
                        {{ day }}
                      </span>
                      <b-tooltip
                        v-if="day && (holidaysByMonth[pagedMonth.index] && holidaysByMonth[pagedMonth.index][day])"
                        :target="`day-${pagedMonth.index}-${day}`"
                        placement="top"
                        triggers="hover"
                        custom-class="vacation-tooltip"
                      >
                        {{ holidaysByMonth[pagedMonth.index][day].type === 'national' ? holidaysByMonth[pagedMonth.index][day].name : `${holidaysByMonth[pagedMonth.index][day].name} (${holidaysByMonth[pagedMonth.index][day].autonomous_community})` }}
                      </b-tooltip>
                      <div
                        v-if="usersByDay[pagedMonth.index] && usersByDay[pagedMonth.index][day]"
                        class="avatars-stack mt-1"
                        style="cursor: pointer;"
                        @click="openDayModal(pagedMonth.index, day)"
                      >
                        <template v-for="(user, idx) in usersByDay[pagedMonth.index][day].slice(0, 5)">
                          <img
                            v-if="user.avatar"
                            :key="'img-' + (user.id || idx)"
                            :src="user.avatar"
                            :alt="user.name"
                            class="avatar-mini"
                            :title="user.name"
                          >
                          <div
                            v-else
                            :key="'init-' + (user.id || idx)"
                            class="avatar-mini avatar-initials-mini"
                            :title="user.name"
                          >
                            {{ user.initials }}
                          </div>
                        </template>
                        <span
                          v-if="usersByDay[pagedMonth.index][day].length > 5"
                          class="more-avatars"
                        >
                          +{{ usersByDay[pagedMonth.index][day].length - 5 }}
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <b-modal
        v-model="showDayModal"
        title="Personas ausentes"
        hide-footer
        size="lg"
      >
        <div v-if="modalUsers.length">
          <template v-for="user in modalUsers">
            <b-link
              v-if="role !== 'operario'"
              :key="`link-${user.id || user.name}`"
              :to="{ name: 'viewUser', params: { id: user.id } }"
              class="d-flex align-items-center mb-2"
            >
              <img
                v-if="user.avatar"
                :src="user.avatar"
                :alt="user.name"
                class="avatar-mini me-2"
              >
              <div
                v-else
                class="avatar-mini avatar-initials-mini me-2"
              >
                {{ user.initials }}
              </div>
              <span class="ml-0_5">{{ user.name }}</span>
              <span
                v-if="user.type"
                class="ml-0_5"
              >
                - {{ user.type.name }}
              </span>
              <span
                v-if="user.requested_days"
                class="ml-0_5"
              >
                ({{ user.requested_days }} días)
              </span>
            </b-link>
            <div
              v-else
              :key="`div-${user.id || user.name}`"
              class="d-flex align-items-center mb-2"
            >
              <img
                v-if="user.avatar"
                :src="user.avatar"
                :alt="user.name"
                class="avatar-mini me-2"
              >
              <div
                v-else
                class="avatar-mini avatar-initials-mini me-2"
              >
                {{ user.initials }}
              </div>
              <span class="ml-0_5">{{ user.name }}</span>
              <template v-if="isPrivacyMode">
                <span
                  v-if="user.privacyLabel"
                  class="ml-0_5"
                >
                  - {{ user.privacyLabel }}
                </span>
              </template>
              <template v-else>
                <span
                  v-if="user.type"
                  class="ml-0_5"
                >
                  - {{ user.type.name }}
                </span>
                <span
                  v-if="user.requested_days"
                  class="ml-0_5"
                >
                  ({{ user.requested_days }} días)
                </span>
              </template>
            </div>
          </template>
        </div>
        <div v-else>
          No hay personas de vacaciones este día.
        </div>
      </b-modal>
    </b-card>
  </b-col>
</template>

<script>
import {
  BLink,
  BCol,
  BRow,
  BFormGroup,
  BCard,
  BButton,
  BTooltip,
} from 'bootstrap-vue'
import BaseSelect from '@/components/ui/select/BaseSelect.vue'
import FilterSidebar from '@/components/ui/FilterSidebar.vue'
import { mapGetters, mapActions } from 'vuex'
const WEEK_DAYS = ['L', 'M', 'X', 'J', 'V', 'S', 'D']

function getWeeksOfMonth(year, month) {
  const weeks = []
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  let week = new Array(7).fill(null)
  let dayOfWeek = (firstDay.getDay() + 6) % 7 // Lunes=0
  for (let day = 1; day <= lastDay.getDate(); day += 1) {
    week[dayOfWeek] = day
    if (dayOfWeek === 6 || day === lastDay.getDate()) {
      weeks.push([...week])
      week = new Array(7).fill(null)
    }
    dayOfWeek = (dayOfWeek + 1) % 7
  }
  return weeks
}

export default {
  components: {
    BButton,
    BFormGroup,
    BLink,
    BaseSelect,
    BCol,
    BRow,
    BCard,
    BTooltip,
    FilterSidebar,
  },
  data() {
    const now = new Date()
    const year = now.getFullYear()
    const currentMonth = now.getMonth()
    return {
      currentYear: year,
      displayedYear: year,
      weekDays: WEEK_DAYS,
      department: null,
      userfilter: null,
      appliedDepartmentId: null,
      appliedUserId: null,
      showFilterSidebar: false,
      months: [],
      currentPage: currentMonth,
      today: {
        year,
        month: currentMonth,
        day: now.getDate(),
      },
      showDayModal: false,
      modalUsers: [],
    }
  },
  computed: {
    ...mapGetters({
      items: 'absences/getItems',
      currentClient: 'clients/getCurrentClient',
      holidays: 'calendar/getHolidays',
      role: 'auth/getRole',
      canCalendarPrivacy: 'clients/getCanModuleCalendarPrivacy',
    }),
    maxPage() {
      return 11
    },
    isPrivacyMode() {
      return this.role === 'operario'
    },
    pagedMonth() {
      return this.months[this.currentPage]
    },
    usersByDay() {
      const result = {}
      if (!Array.isArray(this.items)) return result
      this.items.forEach(req => {
        if (!req.start_date || !req.end_date) return
        const start = new Date(`${req.start_date}T00:00:00`)
        const end = new Date(`${req.end_date}T00:00:00`)
        if (start.getFullYear() > this.displayedYear || end.getFullYear() < this.displayedYear) return
        const d = new Date(Math.max(start.getTime(), new Date(this.displayedYear, 0, 1).getTime()))
        const last = new Date(Math.min(end.getTime(), new Date(this.displayedYear, 11, 31).getTime()))
        while (d <= last) {
          const m = d.getMonth()
          const day = d.getDate()
          if (!result[m]) result[m] = {}
          if (!result[m][day]) result[m][day] = []
          if (req.user && !result[m][day].some(u => u.id === req.user.id)) {
            let privacyLabel = null
            if (this.isPrivacyMode) {
              const typeName = (req.type && req.type.name || '').toLowerCase()
              if (typeName.includes('hora')) {
                privacyLabel = 'Ausente parte del día'
              } else if (typeName.includes('vacac')) {
                privacyLabel = 'Vacaciones'
              } else {
                privacyLabel = 'No disponible hoy'
              }
            }
            result[m][day].push({
              id: req.user.id,
              name: `${req.user.name}${req.user.surname ? ` ${req.user.surname}` : ''}`,
              initials: `${req.user.name ? req.user.name[0] : ''}${req.user.surname ? req.user.surname[0] : ''}`.toUpperCase(),
              avatar: req.user.avatar_url,
              type: req.type,
              requested_days: req.requested_days,
              privacyLabel,
            })
          }
          d.setDate(d.getDate() + 1)
        }
      })
      return result
    },
    holidaysByMonth() {
      const result = {}
      const holidaysData = this.holidays || []
      if (!Array.isArray(holidaysData)) return result

      holidaysData.forEach(holiday => {
        if (!holiday.date) return
        const date = new Date(`${holiday.date}T00:00:00`)
        if (date.getFullYear() !== this.displayedYear) return

        const m = date.getMonth()
        const day = date.getDate()
        if (!result[m]) result[m] = {}

        if (!result[m][day]) {
          result[m][day] = {
            name: holiday.name,
            type: holiday.type,
            autonomous_community: holiday.autonomous_community,
          }
        }
      })
      return result
    },
    hasActiveFilters() {
      return (this.appliedDepartmentId !== null && this.appliedDepartmentId !== '') || (this.appliedUserId !== null && this.appliedUserId !== '')
    },
    computedActiveFilters() {
      const filters = []
      if (this.appliedDepartmentId) {
        filters.push({
          label: 'Departamento',
          value: this.appliedDepartmentId.name,
          icon: 'LayersIcon',
          filterType: 'department',
        })
      }
      if (this.appliedUserId) {
        filters.push({
          label: 'Usuario',
          value: this.appliedUserId.name,
          icon: 'UserIcon',
          filterType: 'user',
        })
      }
      return filters
    },
  },
  watch: {
    displayedYear() {
      this.updateMonths()
      if (this.displayedYear !== this.today.year) {
        this.currentPage = 0
      } else {
        this.currentPage = this.today.month
      }
      this.refreshRequests()
      this.loadHolidays()
    },
    department() {
      this.userfilter = null
    },
  },
  async mounted() {
    await this.updateMonths()
    await this.refreshRequests()
    await this.loadHolidays()
  },
  methods: {
    ...mapActions({
      getListAbsences: 'absences/getListAbsences',
      getHolidays: 'calendar/getHolidays',
    }),
    async refreshRequests() {
      try {
        const month = this.currentPage + 1
        const dateIni = `${this.displayedYear}-${month.toString().padStart(2, '0')}-01`
        const dateFin = `${this.displayedYear}-${month.toString().padStart(2, '0')}-31`
        const departmentId = this.department ? this.department.id : ''
        const userId = this.userfilter ? this.userfilter.id : ''
        await this.getListAbsences({
          page: 1,
          per_page: 999,
          type_id: '',
          user_id: userId,
          date_ini: dateIni,
          date_fin: dateFin,
          status: 'approved',
          client_id: this.currentClient && this.currentClient.id ? this.currentClient.id : '',
          department_id: departmentId,
          include_team: true,
        })
      } catch (error) {
        // Error manejado por el store
      }
    },
    async loadHolidays() {
      try {
        const month = this.currentPage + 1
        const dateIni = `${this.displayedYear}-${month.toString().padStart(2, '0')}-01`
        const dateFin = `${this.displayedYear}-${month.toString().padStart(2, '0')}-31`

        await this.getHolidays({
          start_date: dateIni,
          end_date: dateFin,
          client_id: this.currentClient && this.currentClient.id ? this.currentClient.id : 1,
          year: this.displayedYear,
        })
      } catch (error) {
        // Error manejado por el store
      }
    },
    updateMonths() {
      this.months = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
      ].map((name, idx) => ({
        name,
        index: idx,
        weeks: getWeeksOfMonth(this.displayedYear, idx),
      }))
    },
    isToday(monthIdx, day) {
      return (
        this.today.year === this.displayedYear
        && this.today.month === monthIdx
        && this.today.day === day
      )
    },
    async nextPage() {
      if (this.currentPage < this.maxPage) this.currentPage += 1
      await this.refreshRequests()
      await this.loadHolidays()
    },
    async prevPage() {
      if (this.currentPage > 0) this.currentPage -= 1
      await this.refreshRequests()
      await this.loadHolidays()
    },
    nextYear() {
      if (this.displayedYear < this.currentYear + 1) {
        this.displayedYear += 1
      }
    },
    prevYear() {
      if (this.displayedYear > this.currentYear) {
        this.displayedYear -= 1
      }
    },
    openDayModal(monthIdx, day) {
      this.modalUsers = this.usersByDay[monthIdx][day] || []
      this.showDayModal = true
    },
    handleSearch() {
      this.appliedDepartmentId = this.department
      this.appliedUserId = this.userfilter
      this.refreshRequests()
    },
    clearFilters() {
      this.department = null
      this.userfilter = null
      this.appliedDepartmentId = null
      this.appliedUserId = null
      this.refreshRequests()
    },
    removeActiveFilter(idx) {
      const f = this.computedActiveFilters[idx]
      if (f && f.filterType === 'department') {
        this.appliedDepartmentId = null
        this.department = null
        this.userfilter = null
        this.appliedUserId = null
      } else if (f && f.filterType === 'user') {
        this.appliedUserId = null
        this.userfilter = null
      }
      this.refreshRequests()
    },
    toggleFilterSidebar() {
      this.showFilterSidebar = !this.showFilterSidebar
    },
  },
}
</script>

<style scoped lang="scss">
.table.table-sm td {
  width: 12%;
  background-color: transparent !important;
  border: none !important;
  padding: 0.5em !important;
  box-sizing: border-box;
}

.calendar-day-square {
  max-width: 100%;
  min-height: 90px;
  max-height: 90px;
  width: 100%;
  height: 90px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  margin: 0 auto;
  padding: 2px 0 0 0;
}

.avatars-stack {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  margin-top: 2px;
}

.avatar-mini {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 2px;
  margin-bottom: 2px;
}

.avatar-initials-mini {
  background-color: #ffe8c9;
  color: #ff9f43;
  font-size: 0.6rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.more-avatars {
  background: #eee;
  color: #333;
  border-radius: 50%;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9em;
  cursor: pointer;
  margin-left: 2px;
}

.today {
  background: rgba(156, 206, 217, 0.56);
  color: #608fd3;
  border-radius: 100px;
  padding: 0.2em 0.5em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}

.holiday-day {
  background: #ffd4a3;
  color: #d97706;
  border-radius: 100px;
  padding: 0.2em 0.5em;
  box-sizing: border-box;
  border: 1px solid #ffc078;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  margin: 0 auto;
  cursor: pointer;
  font-weight: 600;
}

</style>

<style lang="scss">
.tooltip.vacation-tooltip {
  opacity: 1 !important;
}

.tooltip.vacation-tooltip .tooltip-inner {
  background-color: #f8f9fa !important;
  color: #212529 !important;
  border: 1px solid #dee2e6 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
  padding: 0.5rem 0.55rem !important;
  font-size: 0.875rem !important;
  max-width: 300px !important;
  text-align: center !important;
  line-height: 1.4 !important;
}

.tooltip.vacation-tooltip .tooltip-inner strong {
  font-weight: 700 !important;
  display: block !important;
  margin-bottom: 0.15rem !important;
}

.tooltip.vacation-tooltip.bs-tooltip-top .arrow::before {
  border-top-color: #dee2e6 !important;
}

.tooltip.vacation-tooltip.bs-tooltip-bottom .arrow::before {
  border-bottom-color: #dee2e6 !important;
}

.tooltip.vacation-tooltip.bs-tooltip-left .arrow::before {
  border-left-color: #dee2e6 !important;
}

.tooltip.vacation-tooltip.bs-tooltip-right .arrow::before {
  border-right-color: #dee2e6 !important;
}
</style>
